// /shares/:inviteId?token=… — 裝置分享邀請落地頁（APP#131；spec 18 §5）。
// 邀請信連結指向此頁（不是帳號驗證頁）。流程（v1 = option b）：
//   未登入 → 提示登入/註冊，完成後再點一次邀請連結（7 天內有效）。
//   已登入 → 顯示接受/婉拒；接受打 POST /v3/shares/{invite_id}/accept（帶 token，
//            後端 email 比對或 token 比對皆可，未註冊佔位接受時自動接回真 user_id）。
// invite token 與帳號驗證 token 為兩條獨立 token（02 §2.2），本頁只碰 invite token。

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError, apiFetch } from '../../lib/api';

type State = 'ready' | 'accepting' | 'ok' | 'declined' | 'expired' | 'error' | 'no-invite';

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
      <section className="card" style={{ width: 'min(420px, 100%)', textAlign: 'center' }}>{children}</section>
    </div>
  );
}

const h1Style = { marginTop: 0, fontSize: 'var(--font-size-3xl)' } as const;
const rowStyle = { display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'center', marginTop: 'var(--spacing-4)' } as const;

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const { inviteId } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>(inviteId ? 'ready' : 'no-invite');
  const [errMsg, setErrMsg] = useState('');

  async function respond(action: 'accept' | 'decline') {
    if (!inviteId) return;
    setState('accepting');
    setErrMsg('');
    try {
      await apiFetch(`/v3/shares/${encodeURIComponent(inviteId)}/${action}`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        body: action === 'accept' && token ? { token } : undefined,
      });
      setState(action === 'accept' ? 'ok' : 'declined');
    } catch (e) {
      if (e instanceof ApiError && (e.status === 410 || e.code === 'INVITE_EXPIRED' || e.code === 'INVITE_NOT_PENDING')) {
        setState('expired');
      } else {
        if (e instanceof ApiError) setErrMsg(`${e.message}（${e.code}）`);
        setState('error');
      }
    }
  }

  if (state === 'no-invite') {
    return (
      <Card>
        <h1 style={h1Style}>{t('shareInvite.title')}</h1>
        <p className="text-secondary">{t('shareInvite.noToken')}</p>
        <Link className="btn-primary" to="/home">{t('shareInvite.toHome')}</Link>
      </Card>
    );
  }

  // option (b): 未登入 → 提示登入/註冊，完成後再點一次信中的邀請連結。
  if (status !== 'authenticated') {
    return (
      <Card>
        <h1 style={h1Style}>{t('shareInvite.title')}</h1>
        <p className="text-secondary">{t('shareInvite.prompt')}</p>
        <p className="text-secondary">{t('shareInvite.needAuth')}</p>
        <div style={rowStyle}>
          <Link className="btn-primary" to="/login">{t('shareInvite.login')}</Link>
          <Link className="btn-ghost" to="/register">{t('shareInvite.register')}</Link>
        </div>
      </Card>
    );
  }

  switch (state) {
    case 'ok':
      return (
        <Card>
          <h1 style={h1Style}>{t('shareInvite.okTitle')}</h1>
          <p className="text-secondary">{t('shareInvite.okBody')}</p>
          <Link className="btn-primary" to="/devices">{t('shareInvite.toDevices')}</Link>
        </Card>
      );
    case 'declined':
      return (
        <Card>
          <h1 style={h1Style}>{t('shareInvite.declinedTitle')}</h1>
          <Link className="btn-primary" to="/home">{t('shareInvite.toHome')}</Link>
        </Card>
      );
    case 'expired':
      return (
        <Card>
          <h1 style={h1Style}>{t('shareInvite.expiredTitle')}</h1>
          <p className="text-secondary">{t('shareInvite.expiredBody')}</p>
          <Link className="btn-primary" to="/home">{t('shareInvite.toHome')}</Link>
        </Card>
      );
    case 'error':
      return (
        <Card>
          <h1 style={h1Style}>{t('shareInvite.errorTitle')}</h1>
          <p className="text-secondary">{t('shareInvite.errorBody')}</p>
          {errMsg && <p className="text-tertiary" style={{ fontSize: 'var(--font-size-sm)' }}>{errMsg}</p>}
          <button className="btn-primary" onClick={() => setState('ready')}>{t('shareInvite.retry')}</button>
        </Card>
      );
    default: // 'ready' | 'accepting'
      return (
        <Card>
          <h1 style={h1Style}>{t('shareInvite.title')}</h1>
          <p className="text-secondary">{t('shareInvite.prompt')}</p>
          <div style={rowStyle}>
            <button className="btn-primary" disabled={state === 'accepting'} onClick={() => void respond('accept')}>
              {state === 'accepting' ? t('common.processing') : t('shareInvite.accept')}
            </button>
            <button className="btn-ghost" disabled={state === 'accepting'} onClick={() => void respond('decline')}>
              {t('shareInvite.decline')}
            </button>
          </div>
        </Card>
      );
  }
}
